import { useState, useEffect, useRef, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSocket } from '../hooks/useSocket';
import { checkoutQueue } from '../utils/checkoutQueue';
import { useOfflineSync, useOnlineStatus } from '../hooks/useOfflineSync';
import { updateTransaction } from '../utils/offlineDb';
import { BUFFET_PRICES, heightLabels } from '../utils/pricing.js';
import { useBranch } from '../contexts/BranchContext';
import Modal from '../components/Modal';
import Toast from '../components/Toast';
import { getPaymentMethodLabel } from '../utils/paymentMethod';
import CashDrawerOpenButton from '../components/CashDrawerOpenButton';
import CashDenominationInput, { DenominationIcon } from '../components/CashDenominationInput';
import CashDenominationTapGrid from '../components/CashDenominationTapGrid';
import { DENOMINATIONS, emptyCounts, sumCounts, countOf, hasAnyCount, denominationLabel, breakdownChange, formatBaht } from '../utils/denominations';

const PAYMENT_METHOD_OPTIONS = [
  { value: 'cash', label: '💵 เงินสด' },
  { value: 'transfer', label: '🏦 โอนเงิน' },
  { value: 'government', label: '🏛️ โครงการรัฐ' }
];

export default function Checkout({ apiUrl }) {
  const { effectiveBranchId, isAllBranches } = useBranch();
  // เช็คบิลเขียนข้อมูลจริงของสาขาเดียวเสมอ ห้ามเผลอทำตอนเลือก "รวมทุกสาขา" เพราะ
  // effectiveBranchId จะ fallback ไปสาขาแรกตามลำดับตัวอักษรแทน ไม่ใช่สาขาที่ตั้งใจ
  const blocked = isAllBranches || !effectiveBranchId;
  const location = useLocation();
  const navigate = useNavigate();
  const HL = heightLabels(BUFFET_PRICES); // ป้ายส่วนสูงตามเกณฑ์สาขา
  const [tables, setTables] = useState([]);
  const [selectedTable, setSelectedTable] = useState(null);
  const [orders, setOrders] = useState([]);
  // รวมบิล — เก็บเงินรอบเดียวกับโต๊ะอื่น แต่ยังสร้าง transaction แยกต่อโต๊ะ
  // เพื่อให้รายงานยอดขาย/จำนวนลูกค้าต่อโต๊ะยังถูกต้องเหมือนเดิม
  const [mergeCodes, setMergeCodes] = useState([]);   // รหัสโต๊ะอื่นที่จ่ายพร้อมกัน
  const [mergeOrders, setMergeOrders] = useState({}); // { [tableCode]: orders[] }
  // โหลดออเดอร์โต๊ะอื่นไม่สำเร็จ — ห้ามเงียบผ่าน เพราะยอดที่โชว์จะขาดเมนูที่สั่งเพิ่มไป
  const [mergeOrdersFailed, setMergeOrdersFailed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text } — แทน alert() เดิม
  const [showCheckoutModal, setShowCheckoutModal] = useState(false);
  // จำค่า "พิมพ์ใบเสร็จ" ไว้ข้ามการเช็คบิลแต่ละครั้ง/ข้ามการโหลดหน้าใหม่ — ติ๊กไว้ยังไง
  // ก็ให้เป็นค่าเริ่มต้นแบบนั้นตลอดจนกว่าจะไปแก้เอง ไม่ต้องมาติ๊กใหม่ทุกโต๊ะ
  const [printReceipt, setPrintReceipt] = useState(() => {
    try {
      return localStorage.getItem('checkoutPrintReceipt') === 'true';
    } catch (_) {
      return false;
    }
  });
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [splitMode, setSplitMode] = useState(false);
  const [splitPayments, setSplitPayments] = useState([
    { method: 'cash', amount: '' },
    { method: 'transfer', amount: '' }
  ]);
  const [isProcessing, setIsProcessing] = useState(false);
  // สถานะแสดงผลอย่างเดียวสำหรับ modal คิดเงิน — ไม่ส่งไป backend เลยสักตัว
  const [showBuffetDetail, setShowBuffetDetail] = useState(false); // กางดูรายการบุฟเฟ่ต์
  const [showMergeSection, setShowMergeSection] = useState(false);  // กางส่วนรวมบิลกับโต๊ะอื่น
  const [cashReceivedCounts, setCashReceivedCounts] = useState(emptyCounts()); // แบงก์/เหรียญที่รับมาจากลูกค้า (ช่องทางหลัก ไม่แบ่งจ่าย)
  const [changeCounts, setChangeCounts] = useState(emptyCounts());             // แบงก์/เหรียญที่ทอนไปจริง (auto แนะนำ แก้ได้)
  const [changeCountsTouched, setChangeCountsTouched] = useState(false);       // พนักงานแก้เงินทอนเองแล้ว — เลิก auto-suggest ทับ
  const [splitCashBreakdown, setSplitCashBreakdown] = useState({});            // { [rowIdx]: { received, change, changeTouched, expanded } } เฉพาะแถวจ่ายเงินสดตอนแบ่งจ่าย
  const [showChangeDetail, setShowChangeDetail] = useState(false);            // กาง/ยุบ "ทอนเป็นแบงก์อะไร" (ช่องทางหลัก) — ค่าเริ่มต้นยุบไว้ ฝั่งรับมาแสดงตลอดไม่ต้องกาง
  const [editingReceived, setEditingReceived] = useState(false);             // กด "← แก้ยอดรับ" ตอนขั้น 2 (เงินทอน) แล้วบังคับกลับไปขั้น 1 (รับเงินมา) แม้ยอดรับจะพอแล้ว — เคลียร์เองทันทีที่แก้ไขยอดรับใหม่ (ดู updateReceivedCounts/clearReceived)
  const [isDesktop, setIsDesktop] = useState(() => window.matchMedia('(min-width: 1024px)').matches);
  const checkoutInProgressRef = useRef(false); // Synchronous guard against double-clicks
  const autoSelectedRef = useRef(false); // เปิด modal จาก navigation state แค่ครั้งเดียว ไม่งั้น socket อัพเดตจะรีเซ็ตโต๊ะที่เลือกรวมไว้ทิ้ง
  const [queueStatus, setQueueStatus] = useState({ active: 0, pending: 0, total: 0 });
  const [billRequest, setBillRequest] = useState(null); // { tableCode, tableName, requestedAt }

  // Offline-first checkout support
  const isOnline = useOnlineStatus();
  const {
    status: offlineStatus,
    addCheckout,
    hasPending,
    hasConflicts,
    sync: triggerOfflineSync
  } = useOfflineSync(apiUrl + '/api', effectiveBranchId);

  // ปิด modal คิดเงิน + ล้างค่าทุกอย่างรวมทั้งโต๊ะที่รวมบิลไว้ — รวมไว้ที่เดียวกันกันลืมรีเซ็ตบางตัว
  // แล้วโต๊ะที่เลือกรวมค้างมาโผล่กับการคิดเงินโต๊ะถัดไป
  const closeCheckoutModal = () => {
    setShowCheckoutModal(false);
    setSelectedTable(null);
    setOrders([]);
    setMergeCodes([]);
    setMergeOrders({});
    setMergeOrdersFailed(false);
    setPaymentMethod('cash');
    setSplitMode(false);
    setCashReceivedCounts(emptyCounts());
    setChangeCounts(emptyCounts());
    setChangeCountsTouched(false);
    setSplitCashBreakdown({});
    setShowChangeDetail(false);
    setEditingReceived(false);
  };

  // WebSocket: Listen for real-time table updates
  useSocket(apiUrl, {
    'table:updated': (data) => {
      console.log('[Checkout] Table updated:', data);
      // Update specific table in state
      setTables(prev => prev.map(table =>
        table.code === data.tableCode ? { ...table, ...data.updates } : table
      ).filter(table => table.status === 'occupied')); // Keep only occupied tables
    },
    'table:closed': (data) => {
      console.log('[Checkout] Table closed:', data);
      // Remove table from list
      setTables(prev => prev.filter(table => table.code !== data.tableCode));
      // If this is the selected table, close modal
      if (selectedTable?.code === data.tableCode) {
        closeCheckoutModal();
      } else {
        // โต๊ะที่รวมบิลไว้ถูกปิดจากเครื่องอื่น — เอาออกจากบิลรวม ไม่งั้นจะเก็บเงินซ้ำ
        setMergeCodes(prev => prev.filter(c => c !== data.tableCode));
      }
    },
    'bill:requested': (data) => {
      console.log('[Checkout] Bill requested:', data);
      // Show popup notification
      setBillRequest(data);
      // Play sound notification
      try {
        const audio = new Audio('data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgodDbq2EcBj+a2telezhZ9f/xnWhIR5Xw/+GDY0JNoff/+5xYSV3v//+MYkxl4+//i2NNaurv8IdlTXHo7/KKZ09z6O7ziWhQdefs9IxpUXjm7PSNalJ65ez0jWtTfOTs9I1rU3zk7PSNa1N85Oz0jWtTfA==');
        audio.volume = 0.5;
        audio.play().catch(() => {});
      } catch (e) {}
    }
  }, {
    fallbackEnabled: false,
    token: localStorage.getItem('adminToken'),
    branchId: effectiveBranchId
  });

  useEffect(() => {
    if (blocked) {
      setLoading(false);
      return;
    }
    loadOccupiedTables();
    // No more polling interval - using WebSocket events instead
  }, [blocked]);

  // Lock body scroll when any modal is open
  useEffect(() => {
    const isAnyModalOpen = showCheckoutModal || !!billRequest;
    document.body.style.overflow = isAnyModalOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [showCheckoutModal, billRequest]);

  // สถานะลิ้นชักเงินสดของสาขา — อ่านอย่างเดียว ใช้โชว์เงินในลิ้นชัก/เตือนตอนรับเงินสด
  // null = ไม่รู้ (ออฟไลน์/โหลดไม่ได้) จะไม่โชว์คำเตือนใด ๆ ไม่ให้เตือนผิดแล้วขวางการคิดเงิน
  const [drawer, setDrawer] = useState(null); // { hasSession, expectedNow }
  useEffect(() => {
    if (!showCheckoutModal || blocked) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${apiUrl}/api/cash-drawer/current`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('adminToken')}` }
        });
        if (!res.ok) throw new Error('drawer unavailable');
        const body = await res.json();
        // stock = แบงก์/เหรียญที่ "ควรมี" ในลิ้นชักตอนนี้ต่อชนิด (ตอนเปิด + รับเข้า − ทอนออก ± เบิก/แลก/เติม)
        // สูตรเดียวกับตาราง "แบงก์ที่ควรมีในลิ้นชักตอนนี้" ใน /cash-drawer — ใช้เสนอเงินทอนเฉพาะชนิดที่มีจริง
        const sess = body.data?.session;
        const flow = body.data?.cashFlow;
        const adj = body.data?.movementsSummary?.otherAdjustments;
        const signed = (counts, v) => { const n = Number(counts?.[String(v)]); return Number.isFinite(n) ? Math.trunc(n) : 0; };
        const stock = (sess && flow)
          ? Object.fromEntries(DENOMINATIONS.map(d => [String(d.value),
              signed(sess.openingCounts, d.value) + signed(flow.received, d.value) - signed(flow.change, d.value) + signed(adj, d.value)]))
          : null;
        if (!cancelled) setDrawer({ hasSession: !!sess, expectedNow: body.data?.expectedNow ?? 0, stock });
      } catch {
        if (!cancelled) setDrawer(null);
      }
    })();
    return () => { cancelled = true; };
  }, [showCheckoutModal, blocked, effectiveBranchId, apiUrl]);

  // ของในลิ้นชักที่ใช้ทอนได้ = ที่ควรมีตอนนี้ + เงินที่ลูกค้าเพิ่งจ่ายมาบิลนี้ (เข้าลิ้นชักแล้วหยิบทอนได้)
  // null = ไม่รู้สถานะลิ้นชัก → ไม่จำกัดชนิด (พฤติกรรมเดิม)
  const drawerAvailableFor = (receivedNow) => {
    if (!drawer?.stock) return null;
    return Object.fromEntries(DENOMINATIONS.map(d => [String(d.value), Math.max(0, (drawer.stock[String(d.value)] || 0) + countOf(receivedNow, d.value))]));
  };
  // ชนิดที่ไม่มีในลิ้นชักเลย ซ่อนจากตัวเลือกทอน (ยกเว้นที่ถูกเลือกค้างอยู่แล้ว จะได้กดลดได้)
  const unavailableChangeValues = (receivedNow, currentChange) => {
    const avail = drawerAvailableFor(receivedNow);
    if (!avail) return [];
    return DENOMINATIONS.filter(d => avail[String(d.value)] <= 0 && countOf(currentChange, d.value) === 0).map(d => d.value);
  };

  // จอกว้าง ≥1024px ใช้ modal คิดเงินแบบ 2 คอลัมน์แทนคอลัมน์เดียว+sticky footer
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 1024px)');
    const handler = (e) => setIsDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  // ESC key closes checkout modal (not bill popup — bill requires explicit action)
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showCheckoutModal && !isProcessing) {
        closeCheckoutModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [showCheckoutModal, isProcessing]);

  // Auto-select table if passed from navigation
  // เปิดแค่ครั้งเดียว — ไม่งั้น tables ที่อัพเดตจาก socket จะ re-run แล้วรีเซ็ต
  // โต๊ะที่พนักงานเพิ่งติ๊กรวมบิลด้วยมือกลับไปเฉยๆ
  useEffect(() => {
    if (autoSelectedRef.current) return;
    if (location.state?.selectedTableCode && tables.length > 0) {
      const table = tables.find(t => t.code === location.state.selectedTableCode);
      if (table) {
        autoSelectedRef.current = true;
        handleSelectTable(table, location.state.mergeTableCodes || []);
      }
    }
  }, [location.state, tables]);

  // Listen to checkout queue status changes
  useEffect(() => {
    const handleStatusChange = (status) => {
      setQueueStatus(status);
    };

    checkoutQueue.onStatusChange(handleStatusChange);

    return () => {
      checkoutQueue.offStatusChange(handleStatusChange);
    };
  }, []);

  // Helper function: Fetch with automatic retry on 429 errors
  const fetchWithRetry = async (url, options = {}, maxRetries = 3) => {
    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(url, options);

        // Handle 429 rate limiting with exponential backoff
        if (response.status === 429) {
          if (attempt < maxRetries - 1) {
            try {
              const errorData = await response.json();
              const waitTime = (errorData.retryAfter || Math.pow(2, attempt)) * 1000;
              console.log(`⏳ Rate limited (attempt ${attempt + 1}/${maxRetries}): waiting ${waitTime}ms before retry...`);
              await new Promise(r => setTimeout(r, waitTime));
              continue; // Try again
            } catch (parseError) {
              console.error('Failed to parse 429 response:', parseError);
            }
          }
        }

        return response; // Return response (success or non-429 error)
      } catch (err) {
        if (attempt === maxRetries - 1) throw err; // Last attempt failed
        const backoffTime = 1000 * Math.pow(2, attempt);
        console.log(`🔄 Network error (attempt ${attempt + 1}/${maxRetries}): retrying in ${backoffTime}ms...`);
        await new Promise(r => setTimeout(r, backoffTime));
      }
    }
  };

  const loadOccupiedTables = async (retryCount = 0) => {
    const MAX_RETRIES = 3;
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/tables`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      // Handle 429 rate limiting errors
      if (response.status === 429) {
        if (retryCount >= MAX_RETRIES) {
          console.error('⚠️ Rate limited too many times, giving up');
          setTables([]);
          setLoading(false);
          return;
        }
        try {
          const errorData = await response.json();
          const retryAfter = errorData.retryAfter || 2;
          console.warn(`⚠️ Rate limited (attempt ${retryCount + 1}/${MAX_RETRIES}), retrying in ${retryAfter} seconds...`);
          await new Promise(r => setTimeout(r, retryAfter * 1000));
          return loadOccupiedTables(retryCount + 1);
        } catch (parseError) {
          console.error('Failed to parse 429 error:', parseError);
          setTables([]);
          return;
        }
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      const data = result.data || result;

      // CRITICAL: Validate data is array before calling .filter()
      if (!Array.isArray(data)) {
        console.error('❌ Expected array from /api/tables, got:', typeof data, data);
        setTables([]);
        return;
      }

      // กรองเฉพาะโต๊ะที่มีลูกค้า
      const occupied = data.filter(table => table.status === 'occupied');
      setTables(occupied);
    } catch (err) {
      console.error('Failed to load tables:', err);
      setTables([]); // Safe fallback to prevent crashes
    } finally {
      setLoading(false);
    }
  };

  const loadTableOrders = async (tableCode) => {
    try {
      const response = await fetch(`${apiUrl}/api/orders?tableCode=${tableCode}`);
      if (!response.ok) {
        console.error(`Failed to load orders: HTTP ${response.status}`);
        return [];
      }
      const result = await response.json();
      const data = result.data || result;
      return Array.isArray(data) ? data : [];
    } catch (err) {
      console.error('Failed to load orders:', err);
      return [];
    }
  };

  // โหลดออเดอร์ของโต๊ะอื่นทั้งหมดรอบเดียว (1 request แทน N) — ช่องรวมบิลจะได้
  // โชว์ยอดจริง (บุฟเฟ่ต์ + เมนูเพิ่ม) ตั้งแต่ก่อนติ๊ก ไม่ใช่ติ๊กแล้วยอดค่อยกระโดด
  const loadOtherTablesOrders = async (excludeCode) => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch(`${apiUrl}/api/orders`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) {
        console.error(`Failed to load orders for merge bill: HTTP ${response.status}`);
        return { grouped: {}, ok: false };
      }
      const result = await response.json();
      const all = result.data || result;
      if (!Array.isArray(all)) return { grouped: {}, ok: false };

      const grouped = {};
      all.forEach(order => {
        const code = order.tableCode;
        if (!code || code === excludeCode) return;
        (grouped[code] = grouped[code] || []).push(order);
      });
      return { grouped, ok: true };
    } catch (err) {
      console.error('Failed to load orders for merge bill:', err);
      return { grouped: {}, ok: false };
    }
  };

  const handleSelectTable = async (table, preselectCodes = []) => {
    setSelectedTable(table);
    const tableOrders = await loadTableOrders(table.code);
    setOrders(tableOrders);

    // โต๊ะที่ถูกส่งมาจากหน้าจัดการโต๊ะ (กดปุ่ม "รวมบิล" มา) — ติ๊กไว้ให้เลย
    const { grouped, ok } = await loadOtherTablesOrders(table.code);
    setMergeOrders(grouped);
    setMergeOrdersFailed(!ok);
    // กรองโต๊ะที่เช็คบิลไม่ได้ออก เผื่อโต๊ะว่างลงระหว่างทางก่อนมาถึงหน้านี้
    const preselect = [...new Set(preselectCodes.filter(c => c && c !== table.code))];
    const filteredPreselect = preselect.filter(c => {
      const t = tables.find(x => x.code === c);
      return t && (calculateGrandTotal(t, grouped[c] || []) > 0 || tableHeadcount(t) > 0);
    });
    setMergeCodes(filteredPreselect);

    // รีเซ็ตสถานะแสดงผลของ modal ใหม่ทุกครั้งที่เปิดโต๊ะ — ไม่ให้ค่าที่กาง/กรอกไว้จากโต๊ะก่อนหน้าค้าง
    setShowBuffetDetail(false);
    setShowMergeSection(filteredPreselect.length > 0);
    setCashReceivedCounts(emptyCounts());
    setChangeCounts(emptyCounts());
    setChangeCountsTouched(false);
    setSplitCashBreakdown({});
    setShowChangeDetail(false);
    setEditingReceived(false);

    setShowCheckoutModal(true);
  };

  const toggleMergeTable = async (code) => {
    if (mergeCodes.includes(code)) {
      setMergeCodes(prev => prev.filter(c => c !== code));
      return;
    }
    // โหลดรวมรอบเดียวพลาดไป → ดึงออเดอร์ของโต๊ะนี้ซ้ำก่อนติ๊ก
    // ไม่งั้น mergeOrders จะว่าง แล้วคิดเงินขาดเมนูที่ลูกค้าสั่งเพิ่มไปทั้งก้อนโดยไม่มีใครรู้
    if (mergeOrdersFailed && !mergeOrders[code]) {
      const loaded = await loadTableOrders(code);
      setMergeOrders(prev => ({ ...prev, [code]: loaded }));
    }
    setMergeCodes(prev => [...prev, code]);
  };

  const calculateTimeElapsed = (startTime) => {
    if (!startTime) return 'ไม่ทราบ';

    const start = new Date(startTime);
    const now = new Date();
    const elapsed = Math.floor((now - start) / 1000 / 60); // minutes

    const hours = Math.floor(elapsed / 60);
    const minutes = elapsed % 60;

    return `${hours} ชม. ${minutes} นาที`;
  };

  const getTimeRemaining = (endTime) => {
    if (!endTime) return null;

    const end = new Date(endTime);
    const now = new Date();
    const remaining = Math.floor((end - now) / 1000 / 60); // minutes

    if (remaining <= 0) return 'หมดเวลา';

    const hours = Math.floor(remaining / 60);
    const minutes = remaining % 60;

    return `${hours} ชม. ${minutes} นาที`;
  };

  const calculateMenuTotal = (orders) => {
    let total = 0;
    orders.forEach(order => {
      if (order.items && Array.isArray(order.items)) {
        order.items.forEach(item => {
          total += (Number(item.price) || 0) * (Number(item.quantity) || 0);
        });
      }
    });
    return total;
  };

  // จัดกลุ่ม/รวมรายการที่ชื่อ+ราคาเดียวกันไว้แสดงผลบน modal คิดเงิน — ไม่แก้ orders ต้นทาง
  // และไม่กรอง item ใดๆ เพิ่มจากเดิม (JSX เดิมวนทุก order.items โดยไม่มีเงื่อนไข ที่นี่ก็เช่นกัน)
  // แยกแค่ price === 0 (บุฟเฟ่ต์ ยุบเป็นบรรทัดเดียว) กับ price > 0 (คิดเงินแยก แสดงเต็ม)
  const { paidItems, buffetLines, buffetLineCount } = useMemo(() => {
    const paidMap = new Map();   // key: name+'|'+price -> {name, price, quantity}
    const buffetMap = new Map(); // key: name -> {name, quantity}
    let lineCount = 0;
    orders.forEach(order => {
      (order.items || []).forEach(item => {
        const price = Number(item.price) || 0;
        const qty = Number(item.quantity) || 0;
        if (price === 0) {
          lineCount += 1;
          const cur = buffetMap.get(item.name) || { name: item.name, quantity: 0 };
          cur.quantity += qty;
          buffetMap.set(item.name, cur);
          return;
        }
        const key = item.name + '|' + price;
        const cur = paidMap.get(key) || { name: item.name, price, quantity: 0 };
        cur.quantity += qty;
        paidMap.set(key, cur);
      });
    });
    return {
      paidItems: [...paidMap.values()],
      buffetLines: [...buffetMap.values()],
      buffetLineCount: lineCount
    };
  }, [orders]);

  const calculateGrandTotal = (table, orders) => {
    // ค่าบุฟเฟ่ต์: pricing.totalPrice มาก่อน buffet.total เสมอ
    // pricing ถูกล้างทุกทางที่ปิดโต๊ะ (checkout / เคลียร์โต๊ะ / ยกเลิกโต๊ะ) ถ้ามีค่าอยู่แปลว่า
    // เป็นของรอบนี้แน่นอน ส่วน buffet ไม่ถูกล้างตอน "เคลียร์โต๊ะ" จึงค้างข้ามรอบได้
    // ถ้าเอา buffet มาก่อน จะคิดเงินด้วยยอดของลูกค้ารอบก่อน และขาดค่ารีฟิลน้ำ (buffet.total ไม่รวมรีฟิล)
    const buffetTotal = table.pricing?.totalPrice ?? table.buffet?.total ?? 0;
    const menuTotal = calculateMenuTotal(orders);
    return buffetTotal + menuTotal;
  };

  // โต๊ะทั้งหมดที่อยู่ในบิลใบนี้ — โต๊ะหลักมาก่อน ตามด้วยโต๊ะที่เลือกรวมตามลำดับที่ติ๊ก
  const getBillTables = () => {
    if (!selectedTable) return [];
    const merged = mergeCodes.map(c => tables.find(t => t.code === c)).filter(Boolean);
    return [selectedTable, ...merged];
  };

  const ordersForTable = (code) => (code === selectedTable?.code ? orders : (mergeOrders[code] || []));

  const billGrandTotal = () =>
    getBillTables().reduce((sum, t) => sum + calculateGrandTotal(t, ordersForTable(t.code)), 0);

  // โต๊ะที่ไม่มีทั้งจำนวนคนและยอดเงิน รวมบิลไม่ได้ — backend ตอบ 400 (emptyCheckoutError)
  // แต่ฝั่งนี้ไม่ throw โต๊ะจะหายจากหน้าจอทั้งที่ยังเปิดอยู่ และคิว offline ค้าง failed
  const tableHeadcount = (table) =>
    (table.guests?.adults || 0) + (table.guests?.children130 || 0)
    + (table.guests?.children120 || 0) + (table.guests?.childrenUnder100 || 0);

  const canMergeTable = (table) =>
    calculateGrandTotal(table, ordersForTable(table.code)) > 0 || tableHeadcount(table) > 0;

  // แบ่งเงินที่เก็บมารอบเดียว ลงแต่ละโต๊ะตามลำดับ — เติมโต๊ะแรกให้เต็มก่อนค่อยไหลไปโต๊ะถัดไป
  // เช่น 2 โต๊ะ 1,639 + 2,732 จ่ายสด 2,000 + โอน 2,371
  // → โต๊ะแรก สด 1,639 | โต๊ะสอง สด 361 + โอน 2,371
  // ยอดรวมต่อช่องทางจึงตรงกับเงินที่รับมาจริง และยอดต่อโต๊ะก็ตรงกับบิลของโต๊ะนั้น
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  const allocatePayments = (payments, totals) => {
    // เดิม pool เก็บแค่ {method, left} ทำให้ cashBreakdown (แบงก์ที่รับมา/ทอนไป ซึ่ง
    // buildPayments() แนบไว้ที่ payments[] ระดับบิลรวม) หายไปทุกครั้งที่ผ่านฟังก์ชันนี้
    // — เกิดกับบิลเงินสดทุกใบ ไม่ว่าจะโต๊ะเดียวหรือรวมหลายโต๊ะ เพราะ checkoutOneTable()
    // เรียก allocatePayments() เสมอ ผลคือหน้า /cash-drawer ไม่เคยเห็นแบงก์ที่รับจริงเลย
    // ทั้งที่แคชเชียร์กรอกครบตามที่ระบบบังคับ — ต้องแนบ cashBreakdown กลับเข้าไปด้วย
    // แนบให้แค่ "ส่วนแบ่งแรก" ที่ดึงจาก payment ช่องทางนั้น (breakdownUsed) เพราะมันคือ
    // การรับเงินก้อนเดียวของบิลรวม ไม่ใช่ของแต่ละโต๊ะแยกกัน — ถ้าบิลแบ่งหลายโต๊ะ แต่ละโต๊ะ
    // จะกลายเป็น transaction คนละเอกสาร แนบซ้ำทุกโต๊ะจะทำให้นับแบงก์ซ้ำตอนรวมยอดลิ้นชัก
    const pool = payments.map(p => ({ method: p.method, left: round2(p.amount), cashBreakdown: p.cashBreakdown, breakdownUsed: false }));
    return totals.map(total => {
      let remaining = round2(total);
      const allocated = [];
      for (const slot of pool) {
        if (remaining <= 0.001) break;
        if (slot.left <= 0.001) continue;
        const take = round2(Math.min(slot.left, remaining));
        slot.left = round2(slot.left - take);
        remaining = round2(remaining - take);
        const entry = { method: slot.method, amount: take };
        if (slot.cashBreakdown && !slot.breakdownUsed) {
          entry.cashBreakdown = slot.cashBreakdown;
          slot.breakdownUsed = true;
        }
        allocated.push(entry);
      }
      // เศษที่เหลือจากการปัด — ยัดเข้าช่องทางแรกของโต๊ะนั้น กันยอดขาดไปเศษสตางค์
      if (remaining > 0.001) {
        if (allocated.length > 0) allocated[0].amount = round2(allocated[0].amount + remaining);
        else allocated.push({ method: pool[0]?.method || paymentMethod, amount: remaining });
      }
      return allocated;
    });
  };

  const splitPaymentsSum = () => splitPayments.reduce((sum, p) => sum + (Number(p.amount) || 0), 0);

  const updateSplitRow = (idx, field, value) => {
    setSplitPayments(prev => prev.map((p, i) => i === idx ? { ...p, [field]: value } : p));
  };

  const addSplitRow = () => {
    setSplitPayments(prev => [...prev, { method: 'cash', amount: '' }]);
  };

  // ลบแถวกลางลิสต์ต้องเลื่อน key ของ splitCashBreakdown ตามไปด้วย (มันผูกกับ "ตำแหน่ง" แถว
  // ไม่ใช่ตัวแถวเอง) ไม่งั้นแบงก์/เหรียญที่กรอกไว้ของแถวถัดจากที่ลบจะไปโผล่ผิดแถว
  const removeSplitRow = (idx) => {
    setSplitPayments(prev => prev.filter((_, i) => i !== idx));
    setSplitCashBreakdown(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const i = Number(k);
        if (i < idx) next[i] = v;
        else if (i > idx) next[i - 1] = v;
      });
      return next;
    });
  };

  const emptySplitBreakdown = { expanded: false, received: emptyCounts(), change: emptyCounts(), changeTouched: false };
  const getSplitBreakdown = (idx) => splitCashBreakdown[idx] || emptySplitBreakdown;

  const toggleSplitBreakdown = (idx) => {
    setSplitCashBreakdown(prev => ({ ...prev, [idx]: { ...getSplitBreakdown(idx), expanded: !getSplitBreakdown(idx).expanded } }));
  };

  // แก้ "รับมา" ของแถวนี้ — auto แนะนำเงินทอนใหม่ตามยอดที่กรอกในแถว (ถ้ายังไม่เคยแก้เงินทอนเอง)
  const updateSplitReceived = (idx, nextReceived) => {
    const cur = getSplitBreakdown(idx);
    const amount = Number(splitPayments[idx]?.amount) || 0;
    const next = { ...cur, received: nextReceived };
    if (!cur.changeTouched) next.change = breakdownChange(Math.max(0, sumCounts(nextReceived) - amount), drawerAvailableFor(nextReceived));
    setSplitCashBreakdown(prev => ({ ...prev, [idx]: next }));
  };

  const updateSplitChange = (idx, nextChange) => {
    setSplitCashBreakdown(prev => ({ ...prev, [idx]: { ...getSplitBreakdown(idx), change: nextChange, changeTouched: true } }));
  };

  // ยอดที่จะส่งไปบันทึกจริง — โหมดปกติ = ช่องทางเดียวเต็มยอด, โหมดแบ่งจ่าย = ตามที่กรอกแต่ละแถว
  // (กรองแถวที่ยังไม่กรอกจำนวนเงินออก กันส่ง amount 0 ปนไปโดยไม่ตั้งใจ)
  const buildPayments = (grandTotal) => {
    if (!splitMode) {
      const entry = { method: paymentMethod, amount: Number(grandTotal) || 0 };
      if (paymentMethod === 'cash' && (hasAnyCount(cashReceivedCounts) || hasAnyCount(changeCounts))) {
        entry.cashBreakdown = { received: cashReceivedCounts, change: changeCounts };
      }
      return [entry];
    }
    return splitPayments
      .map((p, idx) => {
        const entry = { method: p.method, amount: Number(p.amount) || 0 };
        if (p.method === 'cash') {
          const b = splitCashBreakdown[idx];
          if (b && (hasAnyCount(b.received) || hasAnyCount(b.change))) {
            entry.cashBreakdown = { received: b.received, change: b.change };
          }
        }
        return entry;
      })
      .filter(p => p.amount > 0);
  };

  const isSplitValid = (grandTotal) => {
    if (!splitMode) return true;
    const sum = splitPaymentsSum();
    return Math.abs(sum - (Number(grandTotal) || 0)) < 0.01;
  };

  // บังคับกรอกแบงก์/เหรียญที่รับมา "และ" ที่ทอนไปให้ตรงเป๊ะก่อนปิดบิลทุกช่องทางที่เป็นเงินสด —
  // กันพนักงานข้ามการกรอกเพื่อเลี่ยงร่องรอยตอนโกงเงิน (ก่อนหน้านี้ข้ามได้ทั้งคู่ ปิดบิลผ่านปกติ
  // ไม่มีใครรู้ว่าใครข้าม) ทอนไปต้องตรงเป๊ะด้วย ไม่ใช่แค่เตือนเฉยๆ เหมือนตอนแรก — ไม่งั้นพนักงาน
  // กรอกรับมาให้ครบเฉยๆ (ผ่านเงื่อนไข) แล้วทอนไปเท่าไหร่ก็ได้ ยังโกงส่วนทอนได้อยู่ดี
  const isCashBreakdownComplete = () => {
    const rowComplete = (received, change, amount) => {
      if (!hasAnyCount(received) || sumCounts(received) < amount) return false;
      const changeOwed = Math.round((sumCounts(received) - amount) * 100) / 100;
      if (changeOwed <= 0) return true; // จ่ายพอดี ไม่มีเงินทอน ไม่ต้องกรอกฝั่งทอน
      return Math.abs(sumCounts(change) - changeOwed) < 0.001;
    };
    if (!splitMode) {
      if (paymentMethod !== 'cash') return true;
      return rowComplete(cashReceivedCounts, changeCounts, billGrandTotal());
    }
    return splitPayments.every((p, idx) => {
      if (p.method !== 'cash') return true;
      const amount = Number(p.amount) || 0;
      if (amount <= 0) return true; // แถวยอด 0 ถูกกรองออกตอน buildPayments อยู่แล้ว
      const b = getSplitBreakdown(idx);
      return rowComplete(b.received, b.change, amount);
    });
  };

  // เช็คบิลทีละโต๊ะ — บันทึกลง IndexedDB ก่อนเสมอ แล้วค่อยยิง API ปิดโต๊ะ
  // บิลรวมเรียกตัวนี้วนทีละโต๊ะ — ได้ transaction แยกใบต่อโต๊ะ แต่เก็บเงินจริงรอบเดียว
  const checkoutOneTable = async (table, tableOrders, payments, fallbackMethod = 'cash') => {
    // ค่าบุฟเฟ่ต์: pricing.totalPrice มาก่อน buffet.total เสมอ
    // pricing ถูกล้างทุกทางที่ปิดโต๊ะ (checkout / เคลียร์โต๊ะ / ยกเลิกโต๊ะ) ถ้ามีค่าอยู่แปลว่า
    // เป็นของรอบนี้แน่นอน ส่วน buffet ไม่ถูกล้างตอน "เคลียร์โต๊ะ" จึงค้างข้ามรอบได้
    // ถ้าเอา buffet มาก่อน จะคิดเงินด้วยยอดของลูกค้ารอบก่อน และขาดค่ารีฟิลน้ำ (buffet.total ไม่รวมรีฟิล)
    const buffetTotal = table.pricing?.totalPrice ?? table.buffet?.total ?? 0;
    const menuTotal = calculateMenuTotal(tableOrders);
    const grandTotal = buffetTotal + menuTotal;
    // payments ว่างได้เมื่อโต๊ะนี้ยอด 0 บาท (เช่น มีแต่เด็กต่ำกว่า 100cm ที่กินฟรี) — กรณีนี้
    // ห้ามปล่อยให้เป็น 'mixed' เด็ดขาด ไม่งั้นจะได้บิลที่บอกว่าแบ่งจ่ายหลายช่องทาง
    // ทั้งที่ไม่มีช่องทางไหนเลย รายงานแยกตามวิธีจ่ายจะเพี้ยน
    const effectivePaymentMethod = payments.length === 1
      ? payments[0].method
      : payments.length > 1
        ? 'mixed'
        : fallbackMethod;

    console.log(`💳 [Offline-First] บันทึก checkout: โต๊ะ ${table.code} - ยอดรวม: ${grandTotal}฿`);

    // OFFLINE-FIRST: บันทึกลง IndexedDB ก่อนเสมอ
    const offlineId = await addCheckout({
      branchId: effectiveBranchId,
      tableCode: table.code,
      tableName: table.name,
      guests: table.guests || null,
      pricing: table.pricing || null,
      orders: tableOrders,
      buffetTotal,
      menuTotal,
      grandTotal,
      paymentMethod: effectivePaymentMethod,
      payments,
      tableSnapshot: {
        status: table.status,
        sessionId: table.sessionId,
        startTime: table.startTime || table.buffet?.startTime
      }
    });

    console.log(`✅ [Offline-First] บันทึกสำเร็จ: ${offlineId}`);

    // ถ้าออนไลน์ → เรียก API ปิดโต๊ะทันที (ไม่ต้องรอ background sync)
    if (isOnline) {
      try {
        const token = localStorage.getItem('adminToken');
        const requestBody = {
          tableCode: table.code,
          tableName: table.name,
          guests: table.guests || null,
          pricing: table.pricing || null,
          orders: tableOrders,
          buffetTotal,
          menuTotal,
          grandTotal,
          paymentMethod: effectivePaymentMethod,
          payments
        };
        console.log('📤 Sending checkout request:', JSON.stringify(requestBody, null, 2));

        const checkoutResponse = await fetchWithRetry(`${apiUrl}/api/transactions/checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
        });

        const responseData = await checkoutResponse.json();
        console.log(`📥 Checkout response (${checkoutResponse.status}):`, JSON.stringify(responseData, null, 2));

        if (checkoutResponse.ok) {
          // ยิงตรงสำเร็จแล้ว ต้องปิดรายการในคิว offline ทันที ไม่งั้น background sync
          // จะหยิบไปส่งซ้ำที่ /transactions/checkout-offline อีกใบ กลายเป็นบิลซ้ำ 2 ใบ
          // ต่อการเช็คบิล 1 ครั้ง — สอง endpoint นี้กันซ้ำคนละแบบ (ตัวนี้ดูโต๊ะ+เวลา 60 วิ
          // ตัวโน้นดู offlineId) เลยจับคู่กันไม่ได้ ต้องตัดที่ต้นทางตรงนี้
          try {
            await updateTransaction(offlineId, {
              syncStatus: 'synced',
              serverReceiptId: responseData.data?.transaction?.receiptId ?? null,
              serverTransactionId: responseData.data?.transaction?.id
                ?? responseData.data?.transaction?._id ?? null,
              syncedAt: Date.now()
            });
          } catch (markErr) {
            // ปิดคิวไม่สำเร็จ = เสี่ยงได้บิลซ้ำ ต้องเห็นใน log ไม่ใช่เงียบหาย
            console.error('❌ ปิดรายการในคิว offline ไม่สำเร็จ อาจเกิดบิลซ้ำ:', markErr);
          }

          if (responseData.data?.tableCloseWarning) {
            console.warn('⚠️ Checkout สำเร็จ แต่ปิดโต๊ะไม่ได้:', responseData.data.tableCloseWarning);
            setMessage({ type: 'error', text: `ชำระเงินสำเร็จ แต่ระบบปิดโต๊ะไม่ได้ กรุณาปิดโต๊ะ "${table.code}" ด้วยตนเอง` });
          } else {
            console.log('✅ Checkout API สำเร็จ - โต๊ะถูกปิดแล้ว');
          }
          if (responseData.data?.duplicate) {
            console.log('ℹ️ This was a duplicate checkout');
          }
        } else {
          console.warn('⚠️ Checkout API ล้มเหลว แต่บันทึก offline แล้ว');
        }
      } catch (apiErr) {
        console.warn('⚠️ API error แต่บันทึก offline แล้ว:', apiErr.message);
      }
    }

    // พิมพ์ใบเสร็จ (ถ้าเลือกและออนไลน์) — บิลรวมจะได้ใบเสร็จแยกใบต่อโต๊ะ
    // ตรงกับ transaction ที่บันทึกจริง และลูกค้าตรวจยอดของแต่ละโต๊ะได้
    if (printReceipt && isOnline) {
      try {
        const token = localStorage.getItem('adminToken');
        // ยิงผ่าน apiUrl (cloud) เสมอ — cloud proxy ต่อไปเครื่อง daemon จริงของสาขา
        // เองถ้าจำเป็น (ดู printer.route.js) ไม่ต้องรู้/ยิง tunnel ตรงจากเบราว์เซอร์เลย
        const printerResponse = await fetchWithRetry(`${apiUrl}/printer/print-checkout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
            'X-Branch-Id': effectiveBranchId
          },
          body: JSON.stringify({
            table,
            orders: tableOrders,
            buffetTotal,
            menuTotal,
            grandTotal,
            paymentMethod: effectivePaymentMethod,
            payments
          })
        }, 2);

        if (!printerResponse.ok) {
          console.warn('⚠️ การพิมพ์ล้มเหลว');
        } else {
          console.log('✅ พิมพ์ใบเสร็จสำเร็จ');
        }
      } catch (printerErr) {
        console.error('⚠️ Printer error (non-blocking):', printerErr);
      }
    } else if (printReceipt && !isOnline) {
      console.log('⚠️ ออฟไลน์ - จะพิมพ์เมื่อออนไลน์');
    }

    return grandTotal;
  };

  const handleCheckout = async () => {
    // Prevent duplicate submissions - use REF for synchronous check (React state is async)
    if (checkoutInProgressRef.current || isProcessing) {
      console.warn('⚠️ การคิดเงินซ้ำถูกป้องกัน: กำลังประมวลผลอยู่');
      return;
    }

    const billTables = getBillTables();
    if (billTables.length === 0) return;

    const totals = billTables.map(t => calculateGrandTotal(t, ordersForTable(t.code)));
    const grandTotalCheck = totals.reduce((sum, n) => sum + n, 0);
    if (!isSplitValid(grandTotalCheck)) {
      setMessage({ type: 'error', text: `ยอดที่แบ่งจ่าย (${splitPaymentsSum().toFixed(2)} บาท) ไม่ตรงกับยอดที่ต้องชำระ (${grandTotalCheck.toFixed(2)} บาท)` });
      return;
    }
    if (!isCashBreakdownComplete()) {
      setMessage({ type: 'error', text: 'กรุณากรอกแบงก์/เหรียญที่รับมาและที่ทอนไปให้ครบและตรงยอดก่อนปิดบิล (กันร่องรอยเงินในลิ้นชัก)' });
      return;
    }

    // Lock IMMEDIATELY with ref (synchronous) + state (for UI)
    checkoutInProgressRef.current = true;
    setIsProcessing(true);

    const codes = billTables.map(t => t.code);
    const doneCodes = [];

    try {
      const payments = buildPayments(grandTotalCheck);
      const perTablePayments = allocatePayments(payments, totals);
      // วิธีชำระหลักของบิล — ใช้เป็นวิธีจ่ายของโต๊ะที่ไม่ได้รับเงินส่วนไหนเลย (ยอด 0 บาท)
      const primaryMethod = payments[0]?.method || paymentMethod;

      // ทำทีละโต๊ะตามลำดับ — ไม่ยิงพร้อมกัน เพราะ API มี rate limit และกันบิลซ้ำต่อโต๊ะอยู่
      // จดโต๊ะที่สำเร็จไว้ — ถ้าพังกลางคันต้องรู้ว่าโต๊ะไหนเก็บเงินไปแล้ว
      for (let i = 0; i < billTables.length; i++) {
        await checkoutOneTable(billTables[i], ordersForTable(billTables[i].code), perTablePayments[i], primaryMethod);
        doneCodes.push(billTables[i].code);
      }

      // Optimistic UI update - ลบทุกโต๊ะในบิลออกจากรายการทันที
      setTables(prev => prev.filter(t => !codes.includes(t.code)));
      setShowCheckoutModal(false);

      // Log success (ไม่ใช้ alert เพราะมันเป็น blocking และหยุด UI update)
      const statusText = isOnline ? '' : ' (รอซิงค์เมื่อออนไลน์)';
      const label = codes.length > 1
        ? `รวมบิล ${codes.length} โต๊ะ (${codes.join(', ')})`
        : `โต๊ะ ${billTables[0].name}`;
      console.log(`✅ ชำระเงินสำเร็จ: ${label} - ${grandTotalCheck.toFixed(2)} บาท${statusText}`);

      // Reset state before navigating (ป้องกันปุ่ม disabled ค้างถ้า navigation ไม่สำเร็จ)
      checkoutInProgressRef.current = false;
      setIsProcessing(false);
      // กลับไปหน้าจัดการโต๊ะ (พร้อม flag ให้ reload) — เปิดแท็บ "มีลูกค้า" ไว้ให้เลย เพราะ
      // คิดเงินเสร็จโต๊ะหนึ่งแล้ว งานถัดไปมักเป็นดูโต๊ะอื่นที่ยังมีลูกค้าอยู่ ไม่ใช่โต๊ะว่าง
      navigate('/tables', { state: { forceReload: true, closedTable: codes, statusFilter: 'occupied' } });
    } catch (err) {
      console.error(`❌ คิดเงินล้มเหลว: โต๊ะ ${codes.join(', ')}`, err);

      const remaining = codes.filter(c => !doneCodes.includes(c));
      if (doneCodes.length > 0) {
        // โต๊ะที่เก็บเงินไปแล้วต้องหายจากหน้าจอทันที — กันพนักงานกดเก็บซ้ำ
        // (เกราะกันซ้ำฝั่ง backend มีแค่หน้าต่าง 60 วินาที เลยเวลานั้นไปจะออกบิลซ้ำได้จริง)
        setTables(prev => prev.filter(t => !doneCodes.includes(t.code)));
        closeCheckoutModal();
      }

      setMessage({
        type: 'error',
        text:
          `เกิดข้อผิดพลาด: ${err.message}\n` +
          `เก็บเงิน/ปิดโต๊ะสำเร็จแล้ว: ${doneCodes.join(', ') || '— (ยังไม่มี)'}\n` +
          `ยังไม่ได้เก็บ: ${remaining.join(', ')}\n` +
          `กรุณาเก็บเงินเฉพาะโต๊ะที่ยังไม่ได้เก็บ อย่าเก็บโต๊ะที่สำเร็จแล้วซ้ำ`
      });

      // Re-enable button on error
      checkoutInProgressRef.current = false;
      setIsProcessing(false);
    }
  };


  // ===== ส่วนแสดงผลของ modal คิดเงิน — เป็น local render-function ในคอมโพเนนต์นี้เอง
  // (ไม่แยกไฟล์/component ใหม่) ใช้ซ้ำได้ทั้งเลย์เอาต์คอลัมน์เดียว+sticky footer (จอเล็ก)
  // และเลย์เอาต์ 2 คอลัมน์ (จอ ≥1024px) โดยไม่ต้อง duplicate JSX สองชุด =====

  const stickyFooterStyle = {
    position: 'sticky', bottom: 0,
    margin: '20px -2rem -2rem -2rem', padding: '16px 2rem',
    background: 'linear-gradient(135deg, rgba(30,30,45,0.98) 0%, rgba(20,20,30,0.98) 100%)',
    borderTop: '1px solid rgba(255,255,255,0.1)', zIndex: 1
  };

  // เวลาที่นั่ง/เวลาที่เหลือแบบกระชับบรรทัดเดียวใต้ชื่อโต๊ะ — ใช้ calculateTimeElapsed/
  // getTimeRemaining เดิมตรงๆ ไม่แก้ทั้งสองฟังก์ชันนี้เลย สีแดง/เขียวยกมาจากการ์ดโต๊ะ
  // นอก modal (isOvertime scheme เดียวกัน ไม่คิดสีใหม่)
  const renderTimeLine = () => {
    const timeElapsed = calculateTimeElapsed(selectedTable.buffet?.startTime || selectedTable.startTime);
    const timeRemaining = getTimeRemaining(selectedTable.endTime);
    const isOvertime = timeRemaining === 'หมดเวลา';
    return (
      <div style={{
        display: 'inline-block', fontSize: '13px', fontWeight: 600,
        padding: '4px 10px', borderRadius: '6px', marginBottom: '12px',
        background: isOvertime ? 'rgba(239, 91, 91, 0.15)' : 'rgba(63, 191, 127, 0.15)',
        color: isOvertime ? 'var(--danger, #ef5b5b)' : 'var(--ok, #3fbf7f)',
        border: 'none'
      }}>
        {isOvertime ? '⚠️ ' : '⏱️ '}นั่ง {timeElapsed} · เหลือ {timeRemaining}
      </div>
    );
  };

  const renderBuffetCard = () => {
    if (!(selectedTable.buffet || selectedTable.guests)) return null;
    return (
      <div style={{ background: 'var(--surface-2, #1f232c)', border: '1px solid var(--border, #262a35)', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
        <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: 'var(--text, #eceef3)' }}>🍽️ บุฟเฟ่ต์</h3>
        <div style={{ fontSize: '14px', color: 'var(--text-2, #c3c8d4)', marginBottom: '8px' }}>
          {selectedTable.buffet ? (
            <>
              {selectedTable.buffet.adults > 0 && (
                <div>• ผู้ใหญ่: {selectedTable.buffet.adults} คน × {BUFFET_PRICES.adult}฿ = {(selectedTable.buffet.adults * BUFFET_PRICES.adult).toFixed(2)}฿</div>
              )}
              {selectedTable.buffet.childrenBig > 0 && (
                <div>• เด็กโต: {selectedTable.buffet.childrenBig} คน × {BUFFET_PRICES.childBig}฿ = {(selectedTable.buffet.childrenBig * BUFFET_PRICES.childBig).toFixed(2)}฿</div>
              )}
              {selectedTable.buffet.childrenSmall > 0 && (
                <div>• เด็กเล็ก: {selectedTable.buffet.childrenSmall} คน × {BUFFET_PRICES.childSmall}฿ = {(selectedTable.buffet.childrenSmall * BUFFET_PRICES.childSmall).toFixed(2)}฿</div>
              )}
            </>
          ) : (
            <>
              {(selectedTable.guests?.adults || 0) > 0 && (
                <div>• ผู้ใหญ่: {selectedTable.guests?.adults || 0} คน × {BUFFET_PRICES.adult}฿ = {((selectedTable.guests?.adults || 0) * BUFFET_PRICES.adult).toFixed(2)}฿</div>
              )}
              {(selectedTable.guests?.children130 || 0) > 0 && (
                <div>• เด็กโต {HL.childBig}: {selectedTable.guests?.children130 || 0} คน × {BUFFET_PRICES.childBig}฿ = {((selectedTable.guests?.children130 || 0) * BUFFET_PRICES.childBig).toFixed(2)}฿</div>
              )}
              {(selectedTable.guests?.children120 || 0) > 0 && (
                <div>• เด็กเล็ก {HL.childSmall}: {selectedTable.guests?.children120 || 0} คน × {BUFFET_PRICES.childSmall}฿ = {((selectedTable.guests?.children120 || 0) * BUFFET_PRICES.childSmall).toFixed(2)}฿</div>
              )}
              {(selectedTable.guests?.childrenUnder100 || 0) > 0 && (
                <div>• เด็ก {HL.childFree}: {selectedTable.guests?.childrenUnder100 || 0} คน × 0฿ = 0฿</div>
              )}
            </>
          )}
          {selectedTable.pricing?.drinkRefillTotal > 0 && (
            <div>• {selectedTable.pricing.drinkRefillLabel || BUFFET_PRICES.drinkRefillLabel}: {selectedTable.pricing.drinkRefillCount} คน × {selectedTable.pricing.drinkRefillPerHead}฿ = {selectedTable.pricing.drinkRefillTotal.toFixed(2)}฿</div>
          )}
        </div>
        <div style={{ fontSize: '16px', fontWeight: '700', paddingTop: '8px', borderTop: '1px solid var(--border, #262a35)', color: 'var(--text, #eceef3)' }}>
          รวมบุฟเฟ่ต์: {(selectedTable.pricing?.totalPrice ?? selectedTable.buffet?.total ?? 0).toFixed(2)} บาท
        </div>
      </div>
    );
  };

  // เมนูคิดเงินแยก (price > 0, จาก paidItems ที่รวมบรรทัดซ้ำแล้ว) + รายการบุฟเฟ่ต์ (price === 0)
  // ยุบเป็นบรรทัดเดียวกางได้ — ถ้าไม่มีทั้งสองอย่างไม่ต้องแสดงการ์ดนี้เลย
  const renderPaidItemsCard = () => {
    if (paidItems.length === 0 && buffetLineCount === 0) return null;
    return (
      <div style={{ background: 'var(--surface-2, #1f232c)', border: '1px solid var(--border, #262a35)', color: 'var(--text-2, #c3c8d4)', padding: '15px', borderRadius: '8px', marginBottom: '20px' }}>
        {paidItems.length > 0 && (
          <>
            <h3 style={{ margin: '0 0 10px 0', fontSize: '18px', color: 'var(--text, #eceef3)' }}>🍜 เมนูคิดเงินแยก</h3>
            <div>
              {paidItems.map((it, i) => (
                <div key={i} style={{ fontSize: '14px', color: 'var(--text-2, #c3c8d4)', marginBottom: '4px' }}>
                  • {it.name} × {it.quantity} = {(it.price * it.quantity).toFixed(2)}฿
                </div>
              ))}
              <div style={{ fontSize: '16px', fontWeight: '700', paddingTop: '8px', borderTop: '1px solid var(--border, #262a35)', color: 'var(--text, #eceef3)' }}>
                รวมเมนูเพิ่ม: {calculateMenuTotal(orders).toFixed(2)} บาท
              </div>
            </div>
          </>
        )}
        {buffetLineCount > 0 && (
          <div style={{
            marginTop: paidItems.length > 0 ? '12px' : 0,
            paddingTop: paidItems.length > 0 ? '10px' : 0,
            borderTop: paidItems.length > 0 ? '1px solid var(--border, #262a35)' : 'none'
          }}>
            <button
              type="button"
              onClick={() => setShowBuffetDetail(v => !v)}
              style={{ background: 'none', border: 'none', padding: 0, color: 'var(--text-2, #c3c8d4)', fontSize: '13px', cursor: 'pointer', fontWeight: 600 }}
            >
              {showBuffetDetail ? '▾' : '▸'} รายการในบุฟเฟ่ต์ {buffetLineCount} รายการ (รวมในค่าบุฟเฟ่ต์แล้ว)
            </button>
            {showBuffetDetail && (
              <div style={{ marginTop: '6px', paddingLeft: '14px' }}>
                {buffetLines.map((l, i) => (
                  <div key={i} style={{ fontSize: '13px', color: 'var(--muted, #9aa1b2)' }}>• {l.name} × {l.quantity}</div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  // รวมบิลกับโต๊ะอื่น — เก็บเงินรอบเดียว แต่บันทึกแยกใบต่อโต๊ะ ยุบเป็นปุ่มเดียว ค่าเริ่มต้นย่อ
  // แต่ถ้ามีโต๊ะติ๊กไว้แล้วต้องเห็นสรุปเสมอ ไม่ยุบจนมองไม่เห็นว่าเลือกอะไรไว้
  const renderMergeSection = () => {
    if (!tables.some(t => t.code !== selectedTable.code)) return null;
    return (
      <div style={{ marginBottom: '20px' }}>
        {!showMergeSection ? (
          <button
            type="button"
            onClick={() => setShowMergeSection(true)}
            style={{
              width: '100%', textAlign: 'left', padding: '10px 14px', borderRadius: '8px',
              background: 'var(--surface-2, #1f232c)',
              border: mergeCodes.length > 0 ? '2px solid var(--text, #eceef3)' : '1px solid var(--border-strong, #2c3140)',
              color: 'var(--text, #eceef3)', fontWeight: 600, fontSize: '14px', cursor: 'pointer'
            }}
          >
            {mergeCodes.length > 0
              ? `🧾 รวมกับ: โต๊ะ ${mergeCodes.join(', ')} (แก้ไข)`
              : '+ รวมบิลกับโต๊ะอื่น'}
          </button>
        ) : (
          <div style={{
            background: 'var(--surface-2, #1f232c)',
            color: 'var(--text-2, #c3c8d4)',
            padding: '15px',
            borderRadius: '8px',
            border: '1px solid var(--border, #262a35)'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', color: 'var(--text, #eceef3)' }}>🧾 รวมบิลกับโต๊ะอื่น</h3>
              <button
                type="button"
                onClick={() => setShowMergeSection(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-2, #c3c8d4)', cursor: 'pointer', fontSize: '13px', fontWeight: 600 }}
              >
                ย่อ ▴
              </button>
            </div>
            <p style={{ margin: '0 0 10px 0', fontSize: '13px', color: 'var(--muted, #9aa1b2)' }}>
              ติ๊กโต๊ะที่จะจ่ายพร้อมกัน — เก็บเงินรอบเดียว แต่ระบบยังออกบิลแยกใบต่อโต๊ะ
            </p>
            {mergeOrdersFailed && (
              <div style={{
                padding: '10px',
                marginBottom: '10px',
                background: 'rgba(245, 181, 68, 0.15)',
                border: '1px solid var(--warn, #f5b544)',
                borderRadius: '6px',
                fontSize: '13px',
                color: 'var(--warn, #f5b544)',
                fontWeight: '600'
              }}>
                ⚠️ โหลดยอดเมนูเพิ่มของโต๊ะอื่นไม่สำเร็จ — ยอดด้านล่างแสดงเฉพาะค่าบุฟเฟ่ต์
                ระบบจะดึงยอดจริงใหม่ตอนติ๊ก กรุณาตรวจยอดอีกครั้งก่อนกดชำระ
              </div>
            )}
            {tables.filter(t => t.code !== selectedTable.code).map(t => {
              const checked = mergeCodes.includes(t.code);
              const selectable = canMergeTable(t);
              return (
                <label
                  key={t.code}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    padding: '10px',
                    marginBottom: '6px',
                    borderRadius: '6px',
                    background: 'var(--surface, #171a21)',
                    border: checked ? '2px solid var(--text, #eceef3)' : '1px solid var(--border-strong, #2c3140)',
                    cursor: selectable ? 'pointer' : 'not-allowed',
                    opacity: selectable ? 1 : 0.55
                  }}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={!selectable}
                    onChange={() => toggleMergeTable(t.code)}
                    style={{ width: '18px', height: '18px', cursor: selectable ? 'pointer' : 'not-allowed', accentColor: '#667eea' }}
                  />
                  <span style={{ flex: 1, fontWeight: '600', color: 'var(--text, #eceef3)' }}>
                    โต๊ะ {t.code}
                    {!selectable && (
                      <span style={{ display: 'block', fontSize: '12px', fontWeight: '500', color: 'var(--muted, #9aa1b2)' }}>
                        ยังไม่ได้ใส่จำนวนลูกค้า — รวมบิลไม่ได้
                      </span>
                    )}
                  </span>
                  <span style={{ fontSize: '14px', color: 'var(--text-2, #c3c8d4)' }}>
                    {calculateGrandTotal(t, ordersForTable(t.code)).toFixed(2)} บาท
                  </span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // การ์ดยอดรวมสีม่วง — ย้ายเข้า footer/คอลัมน์ขวาแทนที่จะลอยกลางเนื้อหา เพิ่มหัวบรรทัด
  // "โต๊ะ 04 + 02" ตอนรวมบิล (เดิมมีแค่ "(N โต๊ะ)") ส่วน breakdown ต่อโต๊ะด้านล่างเหมือนเดิม
  const renderGrandTotalBlock = () => (
    <div style={{
      background: 'var(--surface-2, #1f232c)',
      border: '1px solid var(--border, #262a35)',
      color: 'var(--text, #eceef3)',
      padding: '20px',
      borderRadius: '8px',
      marginBottom: '20px'
    }}>
      <div style={{ fontSize: '18px', marginBottom: '8px', color: 'var(--text-2, #c3c8d4)' }}>
        ยอดรวมทั้งหมด{mergeCodes.length > 0 ? `: โต๊ะ ${getBillTables().map(t => t.code).join(' + ')}` : ''}
      </div>
      <div style={{ fontSize: '32px', fontWeight: '700', color: 'var(--text, #eceef3)' }}>
        {billGrandTotal().toFixed(2)} บาท
      </div>
      {mergeCodes.length > 0 && (
        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '1px solid var(--border, #262a35)', fontSize: '13px', color: 'var(--text-2, #c3c8d4)' }}>
          {getBillTables().map(t => (
            <div key={t.code} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <span>โต๊ะ {t.code}</span>
              <span>{calculateGrandTotal(t, ordersForTable(t.code)).toFixed(2)} บาท</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  // วิธีชำระเงิน (รวมโหมดแบ่งจ่ายหลายช่องทาง) — ย้าย JSX เดิมมาเฉยๆ ไม่แก้ logic
  const renderPaymentMethodSelector = () => (
    <div style={{ marginBottom: '20px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexWrap: 'wrap', gap: '8px' }}>
        <div style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text, #eceef3)' }}>💳 วิธีชำระเงิน</div>
        <CashDrawerOpenButton apiUrl={apiUrl} style={{ minHeight: '36px', padding: '0 0.9rem', fontSize: '13px' }} />
        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '17px', fontWeight: '600', color: 'var(--text-2, #c3c8d4)', cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={splitMode}
            onChange={(e) => {
              setSplitMode(e.target.checked);
              if (e.target.checked) {
                setSplitPayments([{ method: 'cash', amount: '' }, { method: 'transfer', amount: '' }]);
              }
            }}
            style={{ width: '22px', height: '22px', cursor: 'pointer' }}
          />
          🔀 แบ่งจ่ายหลายช่องทาง
        </label>
      </div>

      {!splitMode ? (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '8px' }}>
          {PAYMENT_METHOD_OPTIONS.map(({ value, label }) => (
            <button
              key={value}
              type="button"
              onClick={() => setPaymentMethod(value)}
              style={{
                padding: '10px 4px',
                border: paymentMethod === value ? '2px solid var(--text, #eceef3)' : '1px solid var(--border-strong, #2c3140)',
                borderRadius: '8px',
                background: 'var(--surface-2, #1f232c)',
                color: paymentMethod === value ? 'var(--text, #eceef3)' : 'var(--text-2, #c3c8d4)',
                fontWeight: paymentMethod === value ? '700' : '500',
                fontSize: '13px',
                whiteSpace: 'nowrap',
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>
      ) : (
        <div style={{ padding: '12px', background: 'var(--surface-2, #1f232c)', borderRadius: '8px', border: '1px solid var(--border, #262a35)' }}>
          {splitPayments.map((p, idx) => {
            const breakdown = getSplitBreakdown(idx);
            const receivedTotal = sumCounts(breakdown.received);
            const changeTotal = sumCounts(breakdown.change);
            const rowAmount = Number(p.amount) || 0;
            return (
            <div key={idx} style={{ marginBottom: '8px' }}>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select
                  value={p.method}
                  onChange={(e) => updateSplitRow(idx, 'method', e.target.value)}
                  style={{ flex: '0 0 140px', padding: '8px', borderRadius: '6px', border: '1px solid var(--border-strong, #2c3140)', background: 'var(--surface, #171a21)', color: 'var(--text, #eceef3)', fontSize: '14px' }}
                >
                  {PAYMENT_METHOD_OPTIONS.map(({ value, label }) => (
                    <option key={value} value={value}>{label}</option>
                  ))}
                </select>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="จำนวนเงิน"
                  value={p.amount}
                  onChange={(e) => updateSplitRow(idx, 'amount', e.target.value)}
                  style={{ flex: 1, padding: '8px', borderRadius: '6px', border: '1px solid var(--border-strong, #2c3140)', background: 'var(--surface, #171a21)', color: 'var(--text, #eceef3)', fontSize: '14px' }}
                />
                {p.method === 'cash' && (
                  <button
                    type="button"
                    onClick={() => toggleSplitBreakdown(idx)}
                    style={{ padding: '8px 10px', background: breakdown.expanded ? 'rgba(102, 126, 234, 0.15)' : 'var(--surface, #171a21)', border: '1px solid #667eea', color: '#667eea', borderRadius: '6px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', whiteSpace: 'nowrap' }}
                  >
                    {breakdown.expanded ? 'ซ่อนแบงก์' : '+ ระบุแบงก์'}
                  </button>
                )}
                {splitPayments.length > 2 && (
                  <button
                    type="button"
                    onClick={() => removeSplitRow(idx)}
                    style={{ padding: '8px 12px', background: 'rgba(239, 91, 91, 0.15)', color: 'var(--danger, #ef5b5b)', border: 'none', borderRadius: '6px', cursor: 'pointer', fontWeight: '600' }}
                  >
                    ✕
                  </button>
                )}
              </div>
              {p.method === 'cash' && breakdown.expanded && (
                <div style={{ marginTop: '6px', padding: '10px', background: 'var(--surface, #171a21)', border: '1px solid var(--border, #262a35)', borderRadius: '8px' }}>
                  <div style={{ fontSize: '12px', fontWeight: 600, marginBottom: '6px', color: 'var(--text-2, #c3c8d4)' }}>รับมา (แบงก์/เหรียญ)</div>
                  <CashDenominationInput counts={breakdown.received} onChange={(next) => updateSplitReceived(idx, next)} />
                  <div style={{ marginTop: '6px', fontSize: '13px', fontWeight: 700, color: 'var(--text, #eceef3)' }}>รวมรับมา: {formatBaht(receivedTotal)} บาท</div>
                  {receivedTotal > rowAmount && (
                    <>
                      <div style={{ fontSize: '12px', fontWeight: 600, margin: '8px 0 6px', color: 'var(--text-2, #c3c8d4)' }}>ทอนไป (แบงก์/เหรียญ)</div>
                      <CashDenominationInput counts={breakdown.change} onChange={(next) => updateSplitChange(idx, next)} excludeValues={unavailableChangeValues(breakdown.received, breakdown.change)} />
                      {Math.abs(changeTotal - (receivedTotal - rowAmount)) > 0.001 && (
                        <div style={{ marginTop: '6px', fontSize: '12px', fontWeight: 600, color: 'var(--danger, #ef5b5b)' }}>
                          ⚠️ ยอดทอน ({formatBaht(changeTotal)}) ไม่ตรงกับเงินทอนที่ต้องจ่าย ({formatBaht(receivedTotal - rowAmount)})
                        </div>
                      )}
                    </>
                  )}
                </div>
              )}
            </div>
            );
          })}
          <button
            type="button"
            onClick={addSplitRow}
            style={{ padding: '6px 12px', background: 'var(--surface, #171a21)', border: '1px dashed #667eea', color: '#667eea', borderRadius: '6px', cursor: 'pointer', fontSize: '13px', fontWeight: '600' }}
          >
            + เพิ่มช่องทาง
          </button>
          {selectedTable && (() => {
            const grandTotal = billGrandTotal();
            const remaining = grandTotal - splitPaymentsSum();
            const ok = Math.abs(remaining) < 0.01;
            return (
              <div style={{ marginTop: '10px', fontSize: '14px', fontWeight: '700', color: ok ? 'var(--ok, #3fbf7f)' : 'var(--danger, #ef5b5b)' }}>
                {ok
                  ? '✅ ยอดครบแล้ว'
                  : remaining > 0
                    ? `คงเหลือ: ${remaining.toFixed(2)} บาท`
                    : `เกินยอด: ${Math.abs(remaining).toFixed(2)} บาท`}
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );

  // เงินในลิ้นชัก + เตือนตอนรับเงินสด: ยังไม่เปิดกะ → เตือน (ไม่ขวางการคิดเงิน) / เปิดกะแล้ว → ไม่บอก
  // ยอด (ลูกค้าหน้าเคาน์เตอร์อาจเห็นจอ ตัวเลขจริงดูที่หน้า /cash-drawer แทน) เตือนเฉพาะตอนเงินทอน
  // มากกว่าที่ควรมีในลิ้นชักก่อนรับแบงก์ใบนี้ (เทียบกับ "ก่อนรับ" เพราะแบงก์ที่ลูกค้ายื่นมาเอามา
  // ทอนตัวเองไม่ได้) — ไม่มีอะไรจะเตือนก็ไม่ต้อง render อะไรเลย
  const renderDrawerNotice = (change) => {
    if (!drawer) return null;
    const box = { marginBottom: '12px', padding: '10px 12px', borderRadius: '8px', fontSize: '13px', fontWeight: 600 };
    if (!drawer.hasSession) {
      return (
        <div style={{ ...box, background: 'rgba(245, 181, 68, 0.15)', border: '1px solid var(--warn, #f5b544)', color: 'var(--warn, #f5b544)' }}>
          ⚠️ ยังไม่ได้เปิดกะเงินทอน — เงินสดบิลนี้จะไม่ถูกนับตอนเทียบยอดปิดร้าน{' '}
          <button
            type="button"
            onClick={() => navigate('/cash-drawer')}
            style={{ marginLeft: '4px', padding: '4px 10px', borderRadius: '6px', border: '1px solid var(--warn, #f5b544)', background: 'var(--surface, #171a21)', color: 'var(--warn, #f5b544)', fontWeight: 700, cursor: 'pointer' }}
          >
            ไปเปิดกะ
          </button>
        </div>
      );
    }
    const notEnough = change > 0 && change > drawer.expectedNow;
    if (!notEnough) return null;
    return (
      <div style={{ ...box, background: 'rgba(239, 91, 91, 0.15)', border: '1px solid var(--danger, #ef5b5b)', color: 'var(--danger, #ef5b5b)' }}>
        ⚠️ เงินในลิ้นชักอาจไม่พอทอน
      </div>
    );
  };

  // เงินทอนตัวใหญ่ — อยู่คอลัมน์ขวาเสมอไม่ว่าจอเล็ก/จอใหญ่ (ที่เดิมของ renderCashCalculator เก่า)
  // ตัดปุ่มลัดยอดออกแล้ว (breakdownChange เดาแบงก์จากยอดรวม ทำให้ข้อมูลแบงก์ในลิ้นชักไม่ตรงกับที่
  // ลูกค้าให้จริง) เหลือแค่ตัวเลขสรุป — ฝั่งกรอกแบงก์จริงอยู่ที่ renderCashDenominationDetail()
  // ด้านล่าง (ย้ายไปคอลัมน์ซ้ายตอนจอกว้าง คอลัมน์ขวากว้างแค่ 340px แคบเกินสำหรับกริด 9 ช่อง)
  const renderCashQuickSection = () => {
    // แบ่งจ่าย: ไม่มีเงินทอนรวม แต่ถ้ามีแถวที่จ่ายเงินสดก็ยังเตือนเรื่องกะที่ยังไม่เปิดได้
    if (splitMode) {
      return splitPayments.some(p => p.method === 'cash') ? renderDrawerNotice(0) : null;
    }
    if (paymentMethod !== 'cash') return null;
    const total = billGrandTotal();
    const received = sumCounts(cashReceivedCounts);
    const change = Math.round((received - total) * 100) / 100;

    return (
      <div style={{ marginBottom: '20px', padding: '12px', background: 'var(--surface-2, #1f232c)', borderRadius: '8px', border: '1px solid var(--border, #262a35)' }}>
        {renderDrawerNotice(change)}
        <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: 'var(--text, #eceef3)' }}>💵 รับเงินมา</div>
        {hasAnyCount(cashReceivedCounts) ? (
          change >= 0 ? (
            <div style={{ fontSize: '20px', fontWeight: 700, color: 'var(--ok, #3fbf7f)' }}>
              เงินทอน {change.toFixed(2)} บาท
            </div>
          ) : (
            <div style={{ fontSize: '18px', fontWeight: 700, color: 'var(--danger, #ef5b5b)' }}>
              ขาดอีก {Math.abs(change).toFixed(2)} บาท
            </div>
          )
        ) : (
          <div style={{ fontSize: '13px', color: 'var(--muted, #9aa1b2)' }}>แตะแบงก์/เหรียญที่ลูกค้ายื่นมาด้านล่าง</div>
        )}
      </div>
    );
  };

  // กริดฝั่ง "รับมา" (แตะการ์ด = +1 แบงก์) — แสดงตลอด ไม่มีปุ่มยุบ/กาง เพราะเป็นทางเดียวที่กรอก
  // ฝั่งนี้ได้แล้ว (ตัดปุ่มลัดออกแล้ว) ไม่บังคับกรอก ข้ามได้ ไม่บล็อกปุ่มชำระเงิน — กรอกไว้แล้วส่งไป
  // เก็บที่ payments[].cashBreakdown จริง (ดู buildPayments) เพื่อเช็คย้อนหลังได้ว่าแบงก์ชนิดไหน
  // ขาด/เกิน ฝั่ง "ทอนไป" ยังใช้ CashDenominationInput (stepper เดิม) ยุบไว้เป็นค่าเริ่มต้น เพราะ
  // พนักงานเลือกเองว่าจะทอนเป็นแบงก์อะไร ไม่ใช่แตะแบงก์ที่ลูกค้ายื่นมาแบบฝั่งรับ — ถูกเรียกคนละ
  // ตำแหน่งใน isDesktop สองสาขาด้านล่าง (จอกว้าง = คอลัมน์ซ้ายใต้การ์ดบุฟเฟ่ต์, จอเล็ก = อยู่ต่อ
  // จาก quick section เดิม) ตัวฟังก์ชันเองไม่ต้องรู้จัก isDesktop เลย
  // 2 ขั้นในการ์ดเดียวกัน — "showStep2" มาจาก state เดิมล้วนๆ (received >= total) ไม่มี state
  // ใหม่กำหนดขั้นเอง ยกเว้น editingReceived (flag ให้กด "← แก้ยอดรับ" ย้อนขั้น 1 ได้ทั้งที่ยอดพอแล้ว)
  // ผลคือถ้ายอดบิลเปลี่ยน (เช่น ติ๊กรวมบิลเพิ่ม) จนยอดรับไม่พอ จะกลับขั้น 1 เองทันทีโดยไม่ต้องเขียน
  // เงื่อนไขแยก เพราะ showStep2 คำนวณใหม่จาก total/received ทุก render อยู่แล้ว
  const renderCashDenominationDetail = () => {
    if (splitMode || paymentMethod !== 'cash') return null;
    const total = billGrandTotal();
    const received = sumCounts(cashReceivedCounts);
    const change = Math.round((received - total) * 100) / 100;
    const changeGivenTotal = sumCounts(changeCounts);
    const isEnough = change >= 0;
    const showStep2 = isEnough && !editingReceived;

    const updateReceivedCounts = (next) => {
      setCashReceivedCounts(next);
      setEditingReceived(false); // แก้ยอดรับใหม่แล้ว ปล่อยให้กฎ auto-switch ตัดสินขั้นใหม่เอง
      if (!changeCountsTouched) {
        setChangeCounts(breakdownChange(Math.max(0, sumCounts(next) - total), drawerAvailableFor(next)));
      }
    };
    const updateChangeCounts = (next) => {
      setChangeCounts(next);
      setChangeCountsTouched(true);
    };
    const clearReceived = () => {
      setCashReceivedCounts(emptyCounts());
      setChangeCounts(emptyCounts());
      setChangeCountsTouched(false);
      setEditingReceived(false);
    };

    const cardStyle = { marginBottom: '20px', padding: '12px', background: 'var(--surface-2, #1f232c)', borderRadius: '8px', border: '1px solid var(--border, #262a35)' };

    // ขั้น 1: รับเงินมา — โชว์ตอนยอดรับยังไม่พอ หรือกด "← แก้ยอดรับ" ค้างไว้จากขั้น 2
    if (!showStep2) {
      return (
        <div style={cardStyle}>
          <div style={{ fontSize: '14px', fontWeight: 600, marginBottom: '8px', color: 'var(--text, #eceef3)' }}>รับเงินมา (แตะแบงก์/เหรียญ)</div>
          <CashDenominationTapGrid counts={cashReceivedCounts} onChange={updateReceivedCounts} />
          <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: '14px', fontWeight: 700, color: isEnough ? 'var(--ok, #3fbf7f)' : 'var(--text, #eceef3)' }}>
              {isEnough
                ? `รับมา ${formatBaht(received)} · เงินทอน ${change.toFixed(2)} บาท`
                : `รับมา ${formatBaht(received)} · ขาดอีก ${Math.abs(change).toFixed(2)} บาท`}
            </div>
            {hasAnyCount(cashReceivedCounts) && (
              <button
                type="button"
                onClick={clearReceived}
                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--danger, #ef5b5b)', fontSize: '13px', fontWeight: 600, cursor: 'pointer' }}
              >
                ล้าง
              </button>
            )}
          </div>
        </div>
      );
    }

    // ขั้น 2: เงินทอน — สรุปยอดรับ + หยิบทอนเป็นแบงก์อะไร (ยุบ stepper ไว้ กางเองอัตโนมัติถ้ายอดไม่ตรง)
    const receivedBreakdown = [...DENOMINATIONS].reverse()
      .filter(d => countOf(cashReceivedCounts, d.value) > 0)
      .map(d => `${denominationLabel(d)} ×${countOf(cashReceivedCounts, d.value)}`)
      .join(', ');
    const changeBreakdownList = [...DENOMINATIONS].reverse().filter(d => countOf(changeCounts, d.value) > 0);
    const mismatch = Math.abs(changeGivenTotal - change) > 0.001;
    const expandChangeStepper = showChangeDetail || mismatch;

    const secondaryBtnStyle = {
      minHeight: '40px',
      padding: '0 16px',
      fontSize: '14px',
      fontWeight: 700,
      background: 'var(--surface, #171a21)',
      border: '1px solid var(--border-strong, #2c3140)',
      borderRadius: '8px',
      color: 'var(--text, #eceef3)',
      cursor: 'pointer'
    };

    const toggleChangeStepper = () => {
      const next = !showChangeDetail;
      setShowChangeDetail(next);
      // เปิดกางแต่ละครั้ง ล้างค่าแบงก์ (ไม่ใช่เหรียญ) ออกก่อนเสมอ — แบงก์ในลิ้นชักมีจำกัด
      // เดา auto จาก breakdownChange แล้วอาจไม่ตรงกับที่มีจริง ให้พนักงานเลือกแบงก์เองทุกครั้ง
      // ต่างจากเหรียญที่มีเหลือเฟือแทบไม่มีวันขาด เก็บค่า auto ไว้ได้ตามเดิม
      if (next) {
        const cleared = { ...changeCounts };
        DENOMINATIONS.filter(d => d.kind === 'note').forEach(d => { cleared[String(d.value)] = ''; });
        setChangeCounts(cleared);
        setChangeCountsTouched(true);
      }
    };

    return (
      <div style={cardStyle}>
        <div style={{ fontSize: change > 0 ? '20px' : '16px', fontWeight: 700, color: change > 0 ? 'var(--ok, #3fbf7f)' : 'var(--text, #eceef3)' }}>
          {change > 0 ? `เงินทอน ${change.toFixed(2)} บาท` : 'รับพอดี ไม่ต้องทอน'}
        </div>
        <div style={{ marginTop: '6px', fontSize: '13px', color: 'var(--text-2, #c3c8d4)' }}>
          รับมา {formatBaht(received)} บาท ({receivedBreakdown})
        </div>

        {change > 0 && (
          <>
            <div style={{ marginTop: '12px', fontSize: '13px', fontWeight: 600, color: 'var(--text, #eceef3)' }}>หยิบทอน:</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '14px', marginTop: '6px' }}>
              {changeBreakdownList.map(d => (
                <div key={d.value} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <DenominationIcon value={d.value} size="lg" />
                  <span style={{ fontSize: '18px', fontWeight: 700, color: 'var(--text, #eceef3)' }}>
                    ×{countOf(changeCounts, d.value)}
                  </span>
                </div>
              ))}
            </div>

            {expandChangeStepper && (
              <div style={{ marginTop: '12px' }}>
                {/* แบงก์ 1000 ไม่ใช่ตัวเลือกที่สมเหตุสมผลตอนทอนเงิน (บิลร้านนี้แทบไม่มีทางทอนถึงหลักพัน) ตัดออก */}
                <CashDenominationInput counts={changeCounts} onChange={updateChangeCounts} excludeValues={[1000, ...unavailableChangeValues(cashReceivedCounts, changeCounts)]} />
                {mismatch && (
                  <div style={{ marginTop: '6px', fontSize: '13px', fontWeight: 600, color: 'var(--danger, #ef5b5b)' }}>
                    ⚠️ ยอดที่เลือกทอน ({formatBaht(changeGivenTotal)} บาท) ไม่ตรงกับเงินทอนที่ต้องจ่าย ({change.toFixed(2)} บาท)
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div style={{ marginTop: '14px', display: 'flex', gap: '8px' }}>
          <button type="button" onClick={() => setEditingReceived(true)} style={secondaryBtnStyle}>
            ← แก้ยอดรับ
          </button>
          {change > 0 && (
            <button type="button" onClick={toggleChangeStepper} style={secondaryBtnStyle}>
              {expandChangeStepper ? 'ซ่อน ▴' : 'แก้แบงก์ทอน ▾'}
            </button>
          )}
        </div>
      </div>
    );
  };

  const renderPrintReceiptCheckbox = () => (
    <div style={{ marginBottom: '20px' }}>
      <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
        <input
          type="checkbox"
          checked={printReceipt}
          onChange={(e) => {
            setPrintReceipt(e.target.checked);
            try {
              localStorage.setItem('checkoutPrintReceipt', String(e.target.checked));
            } catch (_) {}
          }}
          style={{ marginRight: '8px', width: '18px', height: '18px', accentColor: '#667eea' }}
        />
        <span style={{ fontSize: '16px', color: 'var(--text-2, #c3c8d4)', fontWeight: '600' }}>🖨️ พิมพ์ใบเสร็จ</span>
      </label>
    </div>
  );

  const renderActionButtons = () => {
    const cashBreakdownMissing = !isCashBreakdownComplete();
    const checkoutDisabled = isProcessing || !isSplitValid(billGrandTotal()) || cashBreakdownMissing;
    return (
      <div>
      {cashBreakdownMissing && !isProcessing && (
        <div style={{ marginBottom: '8px', fontSize: '13px', color: 'var(--warn, #e0a72e)', fontWeight: 600 }}>
          ⚠️ กรอกแบงก์/เหรียญที่รับมาและที่ทอนไปให้ครบและตรงยอดก่อนปิดบิล
        </div>
      )}
      <div style={{ display: 'flex', gap: '10px' }}>
        <button
          onClick={handleCheckout}
          disabled={checkoutDisabled}
          style={{
            flex: 1,
            padding: '14px',
            background: checkoutDisabled ? '#95a5a6' : '#27ae60',
            color: 'white',
            border: 'none',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            cursor: checkoutDisabled ? 'not-allowed' : 'pointer',
            opacity: checkoutDisabled ? 0.7 : 1
          }}
        >
          {isProcessing
            ? '⏳ กำลังดำเนินการ...'
            : mergeCodes.length > 0
              ? `✅ ชำระเงิน & ปิด ${mergeCodes.length + 1} โต๊ะ`
              : '✅ ชำระเงิน & ปิดโต๊ะ'}
        </button>
        <button
          onClick={closeCheckoutModal}
          style={{
            flex: 1,
            padding: '14px',
            background: 'transparent',
            color: 'var(--text-2, #c3c8d4)',
            border: '1px solid var(--border-strong, #2c3140)',
            borderRadius: '8px',
            fontSize: '14px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            cursor: 'pointer'
          }}
        >
          ยกเลิก
        </button>
      </div>
      </div>
    );
  };

  if (blocked) {
    return (
      <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
        <div style={{ padding: '1.25rem', background: '#fff3cd', color: '#856404', borderRadius: '10px', fontWeight: 600 }}>
          ⚠️ กรุณาเลือกสาขาที่แน่นอนจากเมนูด้านบนก่อน — เช็คบิลต้องระบุสาขาเดียวเสมอ
          จะเลือก "รวมทุกสาขา" ไม่ได้เพื่อป้องกันการทำรายการผิดสาขาโดยไม่ตั้งใจ
        </div>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">กำลังโหลดข้อมูล...</div>;
  }

  return (
    <div>
      <div className="page-header">
        <h2>คิดเงิน / ปิดโต๊ะ</h2>
        <p>คิดเงินและปิดโต๊ะที่มีลูกค้า</p>
      </div>

      <Toast message={message} onDismiss={() => setMessage(null)} />

      {/* Offline Banner */}
      {!isOnline && (
        <div style={{
          background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          boxShadow: '0 2px 8px rgba(245, 158, 11, 0.3)'
        }}>
          <span style={{ fontSize: '24px' }}>📵</span>
          <div>
            <div style={{ fontWeight: '700', fontSize: '16px' }}>ออฟไลน์</div>
            <div style={{ fontSize: '13px', opacity: 0.9 }}>การคิดเงินจะถูกบันทึกและซิงค์อัตโนมัติเมื่อออนไลน์</div>
          </div>
        </div>
      )}

      {/* Sync Status Indicator */}
      {offlineStatus && (offlineStatus.pending > 0 || offlineStatus.conflicts > 0) && (
        <div
          onClick={() => offlineStatus.conflicts > 0 && navigate('/sync-status')}
          style={{
          background: offlineStatus.conflicts > 0
            ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)'
            : offlineStatus.isSyncing
              ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)'
              : 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          marginBottom: '15px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          cursor: offlineStatus.conflicts > 0 ? 'pointer' : 'default'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* ลำดับเช็คต้องตรงกับพื้นหลัง/ข้อความด้านบน (conflicts ต้องมาก่อน isSyncing เสมอ)
                ไม่งั้นตอน sync พื้นหลังทำงานทุก 30 วิ (ถึงจะไม่มีอะไรให้ sync จริงก็ตาม เพราะ
                รายการ conflict ไม่ถูกนับเป็น pending) ไอคอนจะกระพริบเป็นวงหมุนทั้งที่แถบยังแดง
                และข้อความยังบอก "ต้องตรวจสอบ" อยู่ ดูเหมือนค้าง/พัง */}
            {offlineStatus.conflicts > 0 ? (
              <span style={{ fontSize: '20px' }}>⚠️</span>
            ) : offlineStatus.isSyncing ? (
              <div style={{
                width: '20px',
                height: '20px',
                border: '3px solid white',
                borderTopColor: 'transparent',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }} />
            ) : (
              <span style={{ fontSize: '20px' }}>⏳</span>
            )}
            <span style={{ fontSize: '15px', fontWeight: '600' }}>
              {offlineStatus.conflicts > 0
                ? `${offlineStatus.conflicts} รายการต้องตรวจสอบ (คลิกเพื่อดู)`
                : offlineStatus.isSyncing
                  ? `กำลังซิงค์ ${offlineStatus.pending} รายการ...`
                  : `${offlineStatus.pending} รายการรอซิงค์`
              }
            </span>
          </div>
          {!offlineStatus.isSyncing && isOnline && offlineStatus.pending > 0 && (
            <button
              onClick={(e) => { e.stopPropagation(); triggerOfflineSync(); }}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: '1px solid white',
                color: 'white',
                padding: '6px 14px',
                borderRadius: '6px',
                fontSize: '13px',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              🔄 ซิงค์ตอนนี้
            </button>
          )}
        </div>
      )}

      {/* Queue Status Indicator */}
      {queueStatus.total > 0 && (
        <div style={{
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          padding: '12px 20px',
          borderRadius: '8px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <div style={{
              width: '20px',
              height: '20px',
              border: '3px solid white',
              borderTopColor: 'transparent',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <span style={{ fontSize: '16px', fontWeight: '600' }}>
              กำลังประมวลผล {queueStatus.active} โต๊ะ
              {queueStatus.pending > 0 && ` • รออยู่ ${queueStatus.pending} โต๊ะ`}
            </span>
          </div>
          <div style={{
            background: 'rgba(255,255,255,0.2)',
            padding: '4px 12px',
            borderRadius: '12px',
            fontSize: '14px'
          }}>
            {queueStatus.total} รายการทั้งหมด
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(min(100%, 280px), 1fr))',
        gap: '20px',
        marginTop: '20px'
      }}>
        {tables.map((table) => {
          const timeElapsed = calculateTimeElapsed(table.buffet?.startTime || table.startTime);
          const timeRemaining = getTimeRemaining(table.endTime);
          const isOvertime = timeRemaining === 'หมดเวลา';

          return (
            <div
              key={table.code}
              onClick={() => handleSelectTable(table)}
              style={{
                background: 'white',
                // body ตั้งสีตัวอักษรเริ่มต้นเป็น #e0e0e0 (ธีมมืด) — กล่องพื้นสว่างต้องกำหนดสีเอง
                color: '#2d3748',
                padding: '20px',
                borderRadius: '12px',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                cursor: 'pointer',
                border: isOvertime ? '3px solid #e74c3c' : '2px solid #e2e8f0',
                transition: 'all 0.2s'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-4px)';
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
                <h3 style={{ margin: 0, fontSize: '24px' }}>{table.code}</h3>
                <span style={{
                  background: '#f8d7da',
                  color: '#721c24',
                  padding: '4px 12px',
                  borderRadius: '12px',
                  fontSize: '13px',
                  fontWeight: '600'
                }}>
                  มีลูกค้า
                </span>
              </div>

              {(table.buffet || table.guests) && (
                <div style={{
                  background: '#f7fafc',
                  color: '#2d3748',
                  padding: '12px',
                  borderRadius: '8px',
                  marginBottom: '12px'
                }}>
                  <div style={{ fontSize: '14px', marginBottom: '8px', fontWeight: '600' }}>
                    👥 บุฟเฟ่ต์
                  </div>
                  <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                    {table.buffet ? (
                      <>ผู้ใหญ่: {table.buffet.adults} คน | เด็กโต: {table.buffet.childrenBig} คน | เด็กเล็ก: {table.buffet.childrenSmall} คน</>
                    ) : (
                      <>ผู้ใหญ่: {table.guests?.adults || 0} คน | เด็ก: {(table.guests?.children130 || 0) + (table.guests?.children120 || 0) + (table.guests?.childrenUnder100 || 0)} คน</>
                    )}
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: '700', color: '#2d3748' }}>
                    {(table.pricing?.totalPrice ?? table.buffet?.total ?? 0).toFixed(2)} บาท
                  </div>
                </div>
              )}

              <div style={{
                background: isOvertime ? '#fee' : '#e8f5e9',
                padding: '12px',
                borderRadius: '8px',
                marginBottom: '12px'
              }}>
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '4px' }}>
                  ⏱️ เวลาที่นั่ง: {timeElapsed}
                </div>
                <div style={{
                  fontSize: '14px',
                  fontWeight: '600',
                  color: isOvertime ? '#c0392b' : '#27ae60'
                }}>
                  {isOvertime ? '⚠️ ' : '⏰ '}
                  เหลือเวลา: {timeRemaining}
                </div>
              </div>

              <button
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '16px',
                  fontWeight: '600',
                  cursor: 'pointer'
                }}
              >
                💰 คิดเงิน
              </button>
            </div>
          );
        })}

        {tables.length === 0 && (
          <div style={{
            gridColumn: '1 / -1',
            textAlign: 'center',
            padding: '60px 20px',
            color: '#999'
          }}>
            <div style={{ fontSize: '48px', marginBottom: '16px' }}>🍽️</div>
            <h3 style={{ margin: '0 0 8px 0' }}>ไม่มีโต๊ะที่มีลูกค้า</h3>
            <p style={{ margin: 0 }}>โต๊ะทั้งหมดว่างอยู่</p>
          </div>
        )}
      </div>

      {/* Checkout Modal */}
      <Modal
        open={showCheckoutModal && !!selectedTable}
        onClose={closeCheckoutModal}
        maxWidth={isDesktop ? '960px' : '600px'}
      >
            <h2 style={{ marginTop: 0, marginBottom: '4px' }}>
              💰 คิดเงินโต๊ะ {selectedTable?.code}
              {mergeCodes.length > 0 && (
                <span style={{ fontSize: '16px', color: '#8b5cf6', fontWeight: '600' }}>
                  {' '}+ {mergeCodes.join(', ')} (รวมบิล)
                </span>
              )}
            </h2>
            {selectedTable && <div>{renderTimeLine()}</div>}

            {selectedTable && (
              isDesktop ? (
                <div style={{ display: 'flex', gap: '24px', alignItems: 'flex-start' }}>
                  <div style={{ flex: '1 1 60%', minWidth: 0 }}>
                    {renderBuffetCard()}
                    {renderCashDenominationDetail()}
                    {renderPaidItemsCard()}
                    {renderMergeSection()}
                  </div>
                  <div style={{ flex: '0 0 340px', position: 'sticky', top: 0, alignSelf: 'flex-start' }}>
                    {renderGrandTotalBlock()}
                    {renderPaymentMethodSelector()}
                    {renderCashQuickSection()}
                    {renderPrintReceiptCheckbox()}
                    {renderActionButtons()}
                  </div>
                </div>
              ) : (
                <>
                  <div>
                    {renderBuffetCard()}
                    {renderPaidItemsCard()}
                    {renderMergeSection()}
                    {renderPaymentMethodSelector()}
                    {renderCashQuickSection()}
                    {renderCashDenominationDetail()}
                    {renderPrintReceiptCheckbox()}
                  </div>
                  <div style={stickyFooterStyle}>
                    {renderGrandTotalBlock()}
                    {renderActionButtons()}
                  </div>
                </>
              )
            )}
      </Modal>

      {/* Bill Request Popup */}
      {billRequest && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.7)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000,
          animation: 'fadeIn 0.3s ease'
        }}>
          <div style={{
            background: 'linear-gradient(135deg, #ff9800 0%, #f57c00 100%)',
            padding: '40px',
            borderRadius: '20px',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.4)',
            animation: 'bounceIn 0.5s ease',
            maxWidth: '400px',
            width: '90%'
          }}>
            <div style={{ fontSize: '80px', marginBottom: '20px' }}>🔔</div>
            <h2 style={{ color: 'white', margin: '0 0 10px 0', fontSize: '28px' }}>
              ลูกค้าเรียกเก็บเงิน!
            </h2>
            <div style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '15px 25px',
              borderRadius: '12px',
              marginBottom: '25px'
            }}>
              <div style={{ color: 'white', fontSize: '48px', fontWeight: '700' }}>
                โต๊ะ {billRequest.tableCode}
              </div>
              {billRequest.guests && (
                <div style={{ color: 'rgba(255,255,255,0.9)', fontSize: '16px', marginTop: '8px' }}>
                  👥 {(billRequest.guests.adults || 0) + (billRequest.guests.children130 || 0) + (billRequest.guests.children120 || 0)} คน
                </div>
              )}
            </div>
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => {
                  const table = tables.find(t => t.code === billRequest.tableCode);
                  if (table) {
                    handleSelectTable(table);
                  }
                  setBillRequest(null);
                }}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: 'white',
                  color: '#f57c00',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '18px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                💰 ไปคิดเงิน
              </button>
              <button
                onClick={() => setBillRequest(null)}
                style={{
                  flex: 1,
                  padding: '16px',
                  background: 'rgba(255,255,255,0.2)',
                  color: 'white',
                  border: '2px solid white',
                  borderRadius: '12px',
                  fontSize: '18px',
                  fontWeight: '700',
                  cursor: 'pointer'
                }}
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes bounceIn {
          0% { transform: scale(0.5); opacity: 0; }
          50% { transform: scale(1.05); }
          100% { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
}
